import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as PermPostOfferCandidatesPageActions from 'src/app/root-store/perm-post-offer-workflow-store/actions/perm-post-offer-candidates-page.actions';
import { PagingTokenFixed } from 'src/app/shared/models/core';
import { PermanentCandidateItemModel } from 'src/app/shared/models/permanent-candidate-item.model';
import { Observable, of } from 'rxjs';
import { CandidatesService } from 'src/app/shared/lib/common/candidates.service';
import { PermPostOfferService } from '../../../accountmanagers/perm-post-offer/services/perm-post-offer.service';
import { PermanentJobOfferDashboardStatusEnum } from '../../../accountmanagers/perm-post-offer/enums/permanent-job-offer-dashboard-status.enum';
import { Store } from '@ngrx/store';
import { downloadFile } from 'src/app/shared/utilities/download-file';
import { showToasterMessage } from '../../../shared/actions/toaster.actions';
import { PermCandidatesGridMode } from '../../../accountmanagers/candidates/permanent/permanent-candidates-grid.component';
import { PermCandidatesGridFetchArgs } from '../models/perm-post-offer-candidates-grid-filter.model';
import { setJobOfferStatusToOfferAcceptedNotStartingConfirmedSuccess } from '../actions';
import * as permPostOfferCandidateWorkflowSelectors from '../perm-post-offer-candidate-workflow.selectors';

export class PermPostOfferCandidatesPageEffectsToasterMessages {
    static readonly OfferConfirmedSuccessMessage = 'The offer has been confirmed as started.';
    static readonly OfferConfirmedFailureMessage = 'An error occurred while confirming the start, please try again.';
}
@Injectable()
export class PermPostOfferCandidatesPageEffects {
    initializePermanentPreStartCandidates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.initializePermanentCandidatesPreStartPage),
            concatLatestFrom(() => [
                this._store.select(permPostOfferCandidateWorkflowSelectors.selectPreStartCandidatesInformationArgs)
            ]),
            switchMap(([_, candidateInformationArgs]) => {
                const args = structuredClone(candidateInformationArgs);
                args.filter.jobOfferStatusId = PermanentJobOfferDashboardStatusEnum.OfferAcceptedStartDateUnconfirmed;

                return this._candidateService.searchPermanentCandidates(args).pipe(
                    map((result: PagingTokenFixed<PermanentCandidateItemModel[]>) =>
                        PermPostOfferCandidatesPageActions.initializePermanentCandidatesPreStartSuccess({
                            candidates: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(PermPostOfferCandidatesPageActions.initializePermanentCandidatesPreStartFailure({ error }))
                    )
                );
            })
        );
    });

    loadPermanentPreStartCandidates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.loadPermanentCandidatesPreStartPage),
            switchMap((action) => {
                const args = structuredClone(action.args);
                args.filter.jobOfferStatusId = PermanentJobOfferDashboardStatusEnum.OfferAcceptedStartDateUnconfirmed;

                return this._candidateService.searchPermanentCandidates(args).pipe(
                    map((result: PagingTokenFixed<PermanentCandidateItemModel[]>) =>
                        PermPostOfferCandidatesPageActions.loadPermanentCandidatesPreStartSuccess({
                            candidates: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(PermPostOfferCandidatesPageActions.loadPermanentCandidatesPreStartFailure({ error }))
                    )
                );
            })
        );
    });

    initializePermanentConfirmedCandidates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.initializePermanentCandidatesConfirmedPage),
            concatLatestFrom(() => [
                this._store.select(permPostOfferCandidateWorkflowSelectors.selectConfirmedCandidatesInformationArgs)
            ]),
            switchMap(([_, candidateInformationArgs]) => {
                const args = structuredClone(candidateInformationArgs);
                args.filter.jobOfferStatusId = PermanentJobOfferDashboardStatusEnum.OfferAcceptedStartDateConfirmed;

                return this._candidateService.searchPermanentCandidates(args).pipe(
                    map((result: PagingTokenFixed<PermanentCandidateItemModel[]>) =>
                        PermPostOfferCandidatesPageActions.initializePermanentCandidatesConfirmedSuccess({
                            candidates: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(PermPostOfferCandidatesPageActions.initializePermanentCandidatesConfirmedFailure({ error }))
                    )
                );
            })
        );
    });

    loadPermanentConfirmedCandidates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.loadPermanentCandidatesConfirmedPage),
            switchMap((action) => {
                const args = structuredClone(action.args);
                args.filter.jobOfferStatusId = PermanentJobOfferDashboardStatusEnum.OfferAcceptedStartDateConfirmed;

                return this._candidateService.searchPermanentCandidates(args).pipe(
                    map((result: PagingTokenFixed<PermanentCandidateItemModel[]>) =>
                        PermPostOfferCandidatesPageActions.loadPermanentCandidatesConfirmedSuccess({
                            candidates: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(PermPostOfferCandidatesPageActions.loadPermanentCandidatesConfirmedFailure({ error }))
                    )
                );
            })
        );
    });

    setJobOfferStatusToOfferAcceptedStartDateConfirmed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.setJobOfferStatusToConfirmed),
            switchMap((action) =>
                this._permPostOfferService
                    .updatePermanentJobOfferStatus(
                        action.jobOfferId,
                        PermanentJobOfferDashboardStatusEnum.OfferAcceptedStartDateConfirmed
                    )
                    .pipe(
                        map(() => {
                            return PermPostOfferCandidatesPageActions.setJobOfferStatusToConfirmedSuccess();
                        }),
                        catchError((error: unknown) => {
                            return of(
                                PermPostOfferCandidatesPageActions.setJobOfferStatusToConfirmedFailure({ error })
                            );
                        })
                    )
            )
        );
    });

    setJobOfferStatusToOfferAcceptedStartDateConfirmedFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.setJobOfferStatusToConfirmedFailure),
            map(() =>
                showToasterMessage({
                    message: PermPostOfferCandidatesPageEffectsToasterMessages.OfferConfirmedFailureMessage
                })
            )
        );
    });

    setJobOfferStatusToOfferAcceptedStartDateConfirmedSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.setJobOfferStatusToConfirmedSuccess),
            map(() =>
                showToasterMessage({
                    message: PermPostOfferCandidatesPageEffectsToasterMessages.OfferConfirmedSuccessMessage
                })
            )
        );
    });

    refreshPermanentCandidates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                PermPostOfferCandidatesPageActions.refreshPermanentCandidatesPreStartPage,
                PermPostOfferCandidatesPageActions.setJobOfferStatusToConfirmedSuccess,
                setJobOfferStatusToOfferAcceptedNotStartingConfirmedSuccess
            ),
            concatLatestFrom(() => [
                this._store.select(permPostOfferCandidateWorkflowSelectors.selectPreStartCandidatesInformationArgs)
            ]),
            switchMap(([_, CandidatesInformationArgs]) => {
                const args = structuredClone(CandidatesInformationArgs);
                args.filter.jobOfferStatusId = PermanentJobOfferDashboardStatusEnum.OfferAcceptedStartDateUnconfirmed;

                return this._candidateService.searchPermanentCandidates(args).pipe(
                    map((result: PagingTokenFixed<PermanentCandidateItemModel[]>) =>
                        PermPostOfferCandidatesPageActions.refreshPermanentCandidatesSuccess({ candidates: result })
                    ),
                    catchError((error: unknown) =>
                        of(PermPostOfferCandidatesPageActions.refreshPermanentCandidatesFailure({ error }))
                    )
                );
            })
        );
    });

    exportPermanentCandidates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(PermPostOfferCandidatesPageActions.exportPermanentCandidates),
            concatLatestFrom(() => [
                this._store.select(permPostOfferCandidateWorkflowSelectors.selectPreStartCandidatesInformationArgs),
                this._store.select(permPostOfferCandidateWorkflowSelectors.selectConfirmedCandidatesInformationArgs)
            ]),
            switchMap(([action, preStartCandidatesInformationArgs, confirmedCandidatesInformationArgs]) => {
                let apiCall$: Observable<ArrayBuffer>;
                let args: PermCandidatesGridFetchArgs;
                let outputFilename: string;
                switch (action.gridMode) {
                    case PermCandidatesGridMode.PreStart:
                        args = structuredClone(preStartCandidatesInformationArgs);
                        args.filter.permanentJobOfferIds = action.permanentJobOfferIds;
                        apiCall$ = this._candidateService.exportPermanentPreStartCandidates(args);
                        outputFilename = 'PostOfferPermanentPreStartCandidates.xlsx';
                        break;
                    case PermCandidatesGridMode.Confirmed:
                        args = structuredClone(confirmedCandidatesInformationArgs);
                        args.filter.permanentJobOfferIds = action.permanentJobOfferIds;
                        apiCall$ = this._candidateService.exportPermanentConfirmedCandidates(args);
                        outputFilename = 'PostOfferPermanentConfirmedCandidates.xlsx';
                        break;
                }

                return apiCall$.pipe(
                    map((result: ArrayBuffer) => {
                        downloadFile(result, { fileName: outputFilename });
                        return PermPostOfferCandidatesPageActions.exportPermanentCandidatesSuccess();
                    }),
                    catchError((error: unknown) => {
                        return of(PermPostOfferCandidatesPageActions.exportPermanentCandidatesFailure({ error }));
                    })
                );
            })
        );
    });

    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store,
        private readonly _candidateService: CandidatesService,
        private readonly _permPostOfferService: PermPostOfferService
    ) {}
}
